// statRegistry.js — DD-STAT-CONSOLIDATION Phase 0
// SINGLE SOURCE OF TRUTH for stat COLUMN identity: canonical order, labels,
// formatting, per-table slicing, league-scoring-category injection, and the
// per-surface / per-row-type / per-column data-source resolution (live scoring).
//
// Reuses the existing label/field/format SSoT (utils/statMapping STAT_FIELD_MAP)
// rather than redefining keys — Rule 11. Totals math lives in ./statTotals.js.

import {
  STAT_FIELD_MAP,
  getStatConfigs,
  formatStatValue,
} from '../../utils/statMapping';

// =============================================================================
// CANONICAL COLUMN ORDER
// =============================================================================

// HITTING — LOCKED by GM (2026-08-04). Every hitting table renders its relevant
// SLICE of this exact order, with identical labels/order/formatting.
export const HITTING_CANONICAL = [
  'AB', 'R', 'H', '2B', '3B', 'HR', 'RBI', 'BB', 'SB', 'K', 'AVG', 'OBP', 'SLG', 'OPS',
];
export const HITTING_RATE_KEYS = ['AVG', 'OBP', 'SLG', 'OPS'];

// PITCHING — *** PROPOSED, NOT LOCKED *** (pending GM ratification).
// Derived from the UNION of pitching columns across all inventoried surfaces,
// ordered appearances/role → volume → counting-against → rates.
export const PITCHING_CANONICAL_PROPOSED = [
  'GS', 'G', 'W', 'L', 'SV', 'HLD', 'BS', 'QS', 'IP', 'H', 'R', 'ER', 'HR', 'BB', 'SO', 'ERA', 'WHIP',
];
// Extended rate columns seen only on FA search; proposed as OPTIONAL tail.
export const PITCHING_CANONICAL_PROPOSED_EXTENDED = ['K/9', 'BB/9'];
export const PITCHING_RATE_KEYS = ['ERA', 'WHIP', 'K/9', 'BB/9'];

// Identity (non-stat) leading columns — supplied by consumers, listed for slicing.
export const IDENTITY_KEYS = ['POS', 'PLAYER', 'TEAM'];

// Pitching-context field/label defs for the keys STAT_FIELD_MAP lacks in a pitching
// context. Kept HERE (not merged into STAT_FIELD_MAP) so nothing is locked until the
// GM ratifies the pitching set; on ratification these fold into STAT_FIELD_MAP.
export const PITCHING_PROPOSED_FIELDS = {
  G:      { field: 'pitching.games_played',      label: 'G',    decimals: 0, category: 'pitching' },
  H:      { field: 'pitching.hits_allowed',      label: 'H',    decimals: 0, category: 'pitching' },
  R:      { field: 'pitching.runs_allowed',      label: 'R',    decimals: 0, category: 'pitching' },
  HR:     { field: 'pitching.home_runs_allowed', label: 'HR',   decimals: 0, category: 'pitching' },
  BB:     { field: 'pitching.walks_allowed',     label: 'BB',   decimals: 0, category: 'pitching' },
  ERA:    { field: 'pitching.era',               label: 'ERA',  decimals: 2, category: 'pitching' },
  // PROPOSED: WHIP at 3 dp (baseball standard; current STAT_FIELD_MAP shows 2 dp).
  // Pending GM ratification of the pitching set — flagged in the Phase-0 report.
  WHIP:   { field: 'pitching.whip',              label: 'WHIP', decimals: 3, category: 'pitching' },
  'K/9':  { field: 'pitching.k9',                label: 'K/9',  decimals: 2, category: 'pitching' },
  'BB/9': { field: 'pitching.bb9',               label: 'BB/9', decimals: 2, category: 'pitching' },
};

// =============================================================================
// COLUMN CONFIG RESOLUTION
// =============================================================================

/**
 * Resolve one abbrev key to its column config {abbrev, field, label, decimals,
 * prefix, category, isRate}. For pitching, uses the pitching-context override /
 * proposed defs first, then STAT_FIELD_MAP.
 */
export const getColumnConfig = (abbrev, statType = 'hitting') => {
  let base;
  if (statType === 'pitching' && PITCHING_PROPOSED_FIELDS[abbrev]) {
    base = PITCHING_PROPOSED_FIELDS[abbrev];
  } else {
    base = STAT_FIELD_MAP[abbrev];
  }
  if (!base) return null;
  const rateSet = statType === 'pitching' ? PITCHING_RATE_KEYS : HITTING_RATE_KEYS;
  return { abbrev, ...base, isRate: rateSet.includes(abbrev) };
};

/**
 * Select a per-table SLICE of a canonical order, preserving canonical order.
 * Unknown keys are dropped (logged upstream if needed).
 *
 * @param {string[]} selectedKeys - abbrevs to show
 * @param {string} statType - 'hitting' | 'pitching'
 * @returns {Array} ordered column configs
 */
export const sliceColumns = (selectedKeys, statType = 'hitting') => {
  const order = statType === 'pitching'
    ? [...PITCHING_CANONICAL_PROPOSED, ...PITCHING_CANONICAL_PROPOSED_EXTENDED]
    : HITTING_CANONICAL;
  const want = new Set(selectedKeys || []);
  return order
    .filter((k) => want.has(k))
    .map((k) => getColumnConfig(k, statType))
    .filter(Boolean);
};

/**
 * Build a table's column plan = identity columns + injected league scoring
 * categories, from ONE place. Scoring categories come from league settings via
 * the existing getStatConfigs SSoT; they are ALSO reordered to canonical order so
 * every scoring-driven table shows them consistently.
 *
 * @param {string[]} scoringLabels - league scoring category abbrevs
 * @param {string} statType - 'hitting' | 'pitching'
 * @returns {Array} ordered stat column configs (identity handled by the consumer)
 */
export const injectScoringCategories = (scoringLabels, statType = 'hitting') => {
  // Reorder the league's scoring labels into canonical order for consistency,
  // then resolve via the canonical config path (keeps labels/formatting identical
  // to fixed slices). Falls back to getStatConfigs for any non-canonical label.
  const canonicalOrdered = sliceColumns(scoringLabels, statType);
  const covered = new Set(canonicalOrdered.map((c) => c.abbrev));
  const leftover = (scoringLabels || []).filter((l) => !covered.has(l));
  const extras = getStatConfigs(leftover, statType === 'pitching').map((c) => ({
    ...c,
    isRate: (statType === 'pitching' ? PITCHING_RATE_KEYS : HITTING_RATE_KEYS).includes(c.label),
  }));
  return [...canonicalOrdered, ...extras];
};

export { formatStatValue };

// =============================================================================
// PER-SURFACE / PER-ROW-TYPE / PER-COLUMN DATA SOURCE (live-scoring rule)
// =============================================================================
//
// GM LOCKED (2026-08-04): on the LIVE-SCORING surface, PLAYER rows show DAY
// counting stats but SEASON rate stats (a 3-for-4 must never read .750); the
// TEAM TOTALS row is DAY throughout (day counting summed + rates from day totals).
// Every other surface is SEASON throughout. The per-column axis is is-rate.
export const SURFACE_DATASOURCE = {
  live_scoring: {
    player:     { counting: 'day', rate: 'season' },
    team_total: { counting: 'day', rate: 'day' },
  },
  default: {
    player:     { counting: 'season', rate: 'season' },
    team_total: { counting: 'season', rate: 'season' },
  },
};

/**
 * Resolve which stats window a given cell reads.
 * @param {string} surface  - e.g. 'live_scoring' | anything else → default
 * @param {string} rowType  - 'player' | 'team_total'
 * @param {boolean} isRate  - is this a rate column (AVG/OBP/SLG/OPS/ERA/WHIP…)
 * @returns {string} stats window key ('day' | 'season' | …)
 */
export const resolveStatWindow = (surface, rowType, isRate) => {
  const cfg = SURFACE_DATASOURCE[surface] || SURFACE_DATASOURCE.default;
  const rt = cfg[rowType] || SURFACE_DATASOURCE.default[rowType] || SURFACE_DATASOURCE.default.player;
  return isRate ? rt.rate : rt.counting;
};
